import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useMarkNotificationReadForEntity } from "../../context/NotificationContext";
import { Header } from "../../components/vc/Header";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { StatusBadge } from "../../components/vc/StatusBadge";
import { ImageViewer } from "../../components/vc/ImageViewer";
import { ImageStrip } from "../../components/vc/ImageStrip";
import { ChatButton } from "../../components/vc/ChatButton";
import { buildWhatsAppUrl } from "../../lib/phone";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import {
  ArrowLeft, Building2, Calendar, User, PauseCircle, CheckCircle2, ClipboardList, X,
  MessageCircle, Phone, Smartphone, Loader2, PackageCheck, Clock,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_MAP = {
  placed: "pending",
  in_progress: "assigned",
  delivered: "delivered",
  cancelled: "closed",
};

// Phase 7 #6 — same priority set used across Assign Task / Assign Order.
const PRIORITY_OPTIONS = [
  { v: "urgent", l: "Urgent", cls: "bg-red-100 text-red-700 ring-red-300" },
  { v: "high", l: "High", cls: "bg-amber-100 text-amber-700 ring-amber-300" },
  { v: "medium", l: "Medium", cls: "bg-sky-100 text-sky-700 ring-sky-300" },
  { v: "low", l: "Low", cls: "bg-slate-100 text-slate-600 ring-slate-300" },
];

/**
 * Outbound Order Detail (VC style).
 * Replaces the deprecated /pages/OrderDetail.jsx at route /orders/:id.
 * Layout mirrors IncomingOrderDetail so the outbound and inbound workflows share visual language.
 */
export default function VCOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [empId, setEmpId] = useState("");
  const [priority, setPriority] = useState("medium");
  const [assigning, setAssigning] = useState(false);

  const load = () => {
    setLoading(true);
    apiClient.get(`/orders/${id}`)
      .then((r) => {
        if (!r.data) { toast.error("Order not found"); navigate("/orders"); return; }
        setOrder(r.data);
      })
      .catch((err) => {
        if (err.response?.status === 404) toast.error("Order not found");
        else toast.error("Failed to load");
        navigate("/orders");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    apiClient.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {});
  }, [id]);

  // #3 — same as inbound: viewing this order's detail page is enough to
  // clear its notification, regardless of how the user got here.
  useMarkNotificationReadForEntity(id);

  const setStatus = async (status, label) => {
    setBusy(true);
    try {
      const { data } = await apiClient.put(`/orders/${id}`, { status });
      setOrder(data);
      toast.success(label);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const doAssign = async () => {
    if (!empId) { toast.error("Pick an employee"); return; }
    setAssigning(true);
    try {
      const emp = employees.find((e) => e.id === empId);
      // Single call: persists this order's own assignment AND (re)assigns
      // the linked task server-side — reassigning moves the SAME task
      // instead of leaving a stale duplicate on the previous employee's
      // list (Phase 3 #1/#2). This is the order owner's own page, so their
      // employee's contact is always visible to themselves (share_contact
      // true) — no external party sees this page.
      await apiClient.post(`/orders/${order.id}/assignment`, {
        employee_id: empId,
        share_contact: true,
        priority,
      });
      toast.success(`Assigned to ${emp?.name || "employee"}`);
      setAssignOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setAssigning(false);
    }
  };

  const dateStr = useMemo(() => {
    if (!order?.created_at) return "";
    return new Date(order.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }, [order]);

  const waMessage = useMemo(() => {
    if (!order) return "";
    const businessName = user?.business_name || user?.name || "";
    const header = `Namaste ${order.supplier_name || ""} 🙏\n\nPurchase Order · KonexoCRM`;
    const lines = (order.lines && order.lines.length > 0)
      ? order.lines.map((ln, i) => `${i + 1}. ${ln.product} × ${ln.quantity} ${ln.unit} @ ₹${Number(ln.price || 0).toFixed(2)}`)
      : [`1. ${order.product} × ${order.quantity} ${order.unit} @ ₹${Number(order.price || 0).toFixed(2)}`];
    const total = `\nTotal: ₹${Number(order.price || 0).toFixed(2)}`;
    const sign = `\nPlease confirm delivery date. Thank you!${businessName ? `\n\n— ${businessName}` : ""}`;
    return `${header}\n\n${lines.join("\n")}${total}${sign}`;
  }, [order, user]);

  if (loading || !order) {
    return (
      <>
        <Header />
        <div className="px-4 py-8 text-center text-slate-500">Loading...</div>
      </>
    );
  }

  const displayStatus = STATUS_MAP[order.status] || "pending";
  const isTerminal = ["delivered", "cancelled"].includes(order.status);
  const phone = (order.supplier_phone || "").replace(/\D/g, "");
  const waUrl = buildWhatsAppUrl(order.supplier_phone, waMessage);

  const copyMsg = async () => {
    try { await navigator.clipboard.writeText(waMessage); toast.success("Order message copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-32 space-y-4">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} data-testid="order-detail-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <ChatButton orderId={order.id} testid="order-detail-chat-btn" />
        </div>

        {/* Business card */}
        <div data-testid="order-business-card" className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
              <Building2 className="w-5 h-5" strokeWidth={2.2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Supplier</div>
              <div className="font-bold text-slate-900 text-base leading-tight">{order.supplier_name || "Supplier"}</div>
              <div className="text-xs text-slate-500 mt-0.5 font-mono">#{(order.id || "").slice(0, 6).toUpperCase()}</div>
              {order.linked_owner_id && (
                <span data-testid="order-detail-inapp-badge" className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">
                  <Smartphone className="w-3 h-3" /> Placed via app
                </span>
              )}
            </div>
            <StatusBadge status={displayStatus} size="lg" testid="order-detail-status" />
          </div>
        </div>

        {/* Order table */}
        <div data-testid="order-table" className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Order summary</div>
              <div className="font-semibold text-slate-900 text-sm mt-0.5 truncate">{order.product}</div>
            </div>
            {order.lines && order.lines.length > 0 && (
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 tabular-nums shrink-0 ml-2">
                {order.lines.length} item{order.lines.length === 1 ? "" : "s"}
              </div>
            )}
          </div>
          {order.lines && order.lines.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {order.lines.map((ln, i) => {
                const qty = Number(ln.quantity || 0);
                const unitPrice = Number(ln.unit_price != null ? ln.unit_price : (qty ? (Number(ln.price || 0) / qty) : 0));
                const lineTotal = Number(ln.price != null ? ln.price : unitPrice * qty);
                return (
                  <div key={i} className="px-3 py-2 flex items-center gap-2">
                    <span className="w-5 shrink-0 text-center text-[11px] font-bold text-slate-400 tabular-nums">{i + 1}.</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 text-sm truncate">{ln.product}</div>
                      <div className="text-[11px] text-slate-500 tabular-nums">
                        ₹{unitPrice.toFixed(2)} × {qty} {ln.unit}
                      </div>
                    </div>
                    <div data-testid={`order-line-total-${i}`} className="text-sm font-bold text-slate-900 shrink-0 tabular-nums">₹{lineTotal.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-3 py-2 flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-900 font-semibold truncate">{order.quantity} {order.unit}</div>
                {order.unit_price ? (
                  <div className="text-[11px] text-slate-500 tabular-nums">
                    ₹{Number(order.unit_price).toFixed(2)} × {order.quantity} {order.unit}
                  </div>
                ) : null}
              </div>
              <div className="text-sm font-bold text-slate-900 shrink-0 tabular-nums">₹{Number(order.price || 0).toFixed(2)}</div>
            </div>
          )}
          <div className="px-4 py-2.5 bg-emerald-50 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">Total</div>
            <div className="text-lg font-bold text-emerald-900 tabular-nums">₹{Number(order.price || 0).toFixed(2)}</div>
          </div>
        </div>

        {/* Info */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-2.5 shadow-sm">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Order information</div>
          <InfoRow Icon={Calendar} label="Placed on" value={dateStr} />
          {(order.expected_delivery_date || order.expected_delivery_time) && (
            <InfoRow
              Icon={Clock}
              label="Expected delivery"
              value={[order.expected_delivery_date, order.expected_delivery_time].filter(Boolean).join(" · ")}
              testid="order-delivery-time"
            />
          )}
          {order.supplier_name && <InfoRow Icon={User} label="Supplier" value={order.supplier_name} />}
          {order.customer_name && <InfoRow Icon={User} label="Customer" value={order.customer_name} />}
          {order.notes && <InfoRow Icon={ClipboardList} label="Notes" value={order.notes} />}
          {/* Assigned employee (supplier-side control). Name is always shown
              once the supplier assigns someone; phone/call icon appears only
              when the supplier ticks "Share contact with customer". */}
          {order.assigned_employee_name && (
            <div className="pt-2 border-t border-slate-100" data-testid="order-assigned-employee">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Assigned to</div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">{order.assigned_employee_name}</span>
                {order.share_employee_contact && order.assigned_employee_phone && (
                  <a
                    href={`tel:${order.assigned_employee_phone}`}
                    data-testid="order-assigned-employee-call"
                    className="w-10 h-10 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-700 grid place-items-center"
                    aria-label="Call employee"
                  >
                    <Phone className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {order.images && order.images.length > 0 ? (
          <div data-testid="order-images-block" className="bg-white rounded-2xl border border-slate-100 p-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-1 pb-2">
              {order.images.length} attached {order.images.length === 1 ? "image" : "images"}
            </div>
            <ImageStrip images={order.images} testid="order-image-strip" />
          </div>
        ) : (order.image_data && (
          <ImageViewer src={order.image_data} label="Order image" testid="order-detail-image" />
        ))}

        {/* Send order to supplier */}
        <div data-testid="send-order-panel" className="bg-white rounded-2xl border-2 border-emerald-200 p-4 space-y-3 shadow-sm">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-700" />
            <h3 className="text-sm font-bold text-slate-900">Send order to supplier</h3>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 text-xs font-mono whitespace-pre-wrap text-slate-700 max-h-40 overflow-y-auto border border-slate-100">
            {waMessage}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <SecondaryButton onClick={copyMsg} data-testid="order-copy-msg" className="flex items-center justify-center gap-1">
              Copy message
            </SecondaryButton>
            <a
              href={waUrl || "#"}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => { if (!waUrl) { e.preventDefault(); toast.error("Supplier phone number looks invalid"); } }}
              data-testid="order-whatsapp-btn"
              className="h-11 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm flex items-center justify-center gap-2 transition"
            >
              <MessageCircle className="w-4 h-4" /> Send
            </a>
          </div>
          {order.supplier_phone && (
            <a
              href={`tel:${phone}`}
              data-testid="order-call-btn"
              className="h-11 rounded-2xl border border-slate-200 hover:bg-slate-50 text-slate-800 font-semibold text-sm flex items-center justify-center gap-2"
            >
              <Phone className="w-4 h-4" /> Call {order.supplier_name}
            </a>
          )}
        </div>

        {/* Status controls */}
        {!isTerminal && (
          <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-2 shadow-sm">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Update status</div>
            <div className="grid grid-cols-2 gap-2">
              {order.status === "placed" && (
                <PrimaryButton
                  onClick={() => setStatus("in_progress", "Marked in progress")}
                  loading={busy}
                  data-testid="order-mark-inprogress"
                  className="col-span-2 flex items-center justify-center gap-2"
                >
                  <Loader2 className="w-4 h-4" /> Mark In Progress
                </PrimaryButton>
              )}
              {order.status === "in_progress" && (
                <PrimaryButton
                  onClick={() => setStatus("delivered", "Order delivered")}
                  loading={busy}
                  data-testid="order-mark-delivered"
                  className="col-span-2 flex items-center justify-center gap-2"
                >
                  <PackageCheck className="w-4 h-4" /> Mark Delivered
                </PrimaryButton>
              )}
              <SecondaryButton
                onClick={() => setStatus("cancelled", "Order cancelled")}
                disabled={busy}
                data-testid="order-mark-cancelled"
                className="col-span-2 flex items-center justify-center gap-2"
              >
                <PauseCircle className="w-4 h-4" /> Cancel Order
              </SecondaryButton>
            </div>
          </div>
        )}

        {/* Assign to employee */}
        <button
          onClick={() => setAssignOpen(true)}
          data-testid="order-detail-assign-employee"
          className="w-full h-14 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-sm shadow-indigo-500/30 transition active:scale-[0.98]"
        >
          <ClipboardList className="w-5 h-5" />
          {order.assigned_employee_name ? "Reassign to another employee" : "Assign to Employee"}
        </button>
      </div>

      {/* Assign employee sheet */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle>{order.assigned_employee_name ? "Reassign to Another Employee" : "Assign to Employee"}</DialogTitle>
            <DialogDescription>
              {order.assigned_employee_name
                ? `Currently assigned to ${order.assigned_employee_name}. Pick a new team member to take over.`
                : "Pick a team member and they will get this in their tasks."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {employees.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6">
                No employees yet.{" "}
                <button onClick={() => navigate("/employees")} className="text-emerald-700 font-semibold underline">Add one</button>
              </div>
            ) : (
              <>
                <Select value={empId || "none"} onValueChange={(v) => setEmpId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="assign-emp-select" className="h-12 rounded-2xl bg-white">
                    <SelectValue placeholder="Choose employee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Choose employee —</SelectItem>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}{e.role ? ` · ${e.role}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Priority</div>
                  <div className="grid grid-cols-4 gap-1.5">
                    {PRIORITY_OPTIONS.map((p) => (
                      <button
                        key={p.v}
                        type="button"
                        onClick={() => setPriority(p.v)}
                        data-testid={`assign-priority-${p.v}`}
                        className={`h-9 rounded-xl text-[11px] font-bold transition ring-2 ${priority === p.v ? p.cls + " ring-offset-1" : "bg-white text-slate-400 ring-slate-100"}`}
                      >
                        {p.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <SecondaryButton onClick={() => setAssignOpen(false)} data-testid="assign-cancel" className="flex items-center justify-center gap-1">
                    <X className="w-4 h-4" /> Cancel
                  </SecondaryButton>
                  <PrimaryButton onClick={doAssign} loading={assigning} data-testid="assign-confirm">
                    <CheckCircle2 className="w-4 h-4 inline mr-1" /> Assign
                  </PrimaryButton>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function InfoRow({ Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-2.5" data-testid={testid}>
      <Icon className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" strokeWidth={2.2} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</div>
        <div className="text-sm text-slate-900 mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}
